import {
  formatCivilDate,
  type ReportFilterClause,
  type ReportFilterValue,
  type ReportFilters,
} from '@eis/core/reporting';
import { displayFieldName } from '@eis/patterns/reporting';

/** How the viewer shows an operator (mockup board 3): `=`, `≥`, `∈` … */
const SYMBOL: Readonly<Record<string, string>> = {
  '==': '=',
  '!=': '≠',
  '>': '>',
  '<': '<',
  '>=': '≥',
  '<=': '≤',
  '=@': '∋',
  '!@': '∌',
  '=~': '~',
  '!~': '≁',
  '=()': '∈',
  '!()': '∉',
  '==$null': '= ∅',
  '!=$null': '≠ ∅',
};

export interface FilterToken {
  readonly index: number;
  readonly label: string;
  readonly text: string;
  readonly editable: boolean;
  /** The URL carries a value for it (D-2: `f.<index>`). */
  readonly changed: boolean;
}

/** A filter value as a reader sees it: an RFC 3339 instant or a civil date as a date. */
export function showValue(value: string, locale: string): string {
  if (/^\d{4}-\d{2}-\d{2}(T00:00:00Z)?$/.test(value))
    return formatCivilDate(value.slice(0, 10), locale);
  return value;
}

export function clauseText(clause: ReportFilterClause, locale: string): string {
  const symbol = SYMBOL[clause.op] ?? clause.op;
  if (clause.op === '==$null' || clause.op === '!=$null') return symbol;
  const values = clause.values ?? (clause.value === undefined ? [] : [clause.value]);
  return `${symbol} ${values.map((v) => showValue(v, locale)).join(', ')}`;
}

/**
 * The viewer's tokens (U-4): every clause with the viewer's value applied; a fixed one is shown
 * dashed, an editable one carries its value from the URL.
 */
export function filterTokens(
  saved: ReportFilters,
  applied: ReportFilters,
  values: readonly ReportFilterValue[],
  locale: string,
): FilterToken[] {
  return applied.clauses.map((clause, index) => ({
    index,
    label: clause.label ?? saved.clauses[index]?.label ?? displayFieldName(clause.field),
    text: clauseText(clause, locale),
    editable: clause.editable === true,
    changed: values.some((v) => v.index === index),
  }));
}

/** The text a reader types for a value back into the clause's form (a civil date → RFC 3339). */
export function valueForClause(clause: ReportFilterClause, typed: string): string {
  const original = clause.values?.[0] ?? clause.value ?? '';
  if (original.endsWith('T00:00:00Z') && /^\d{4}-\d{2}-\d{2}$/.test(typed))
    return `${typed}T00:00:00Z`;
  const civil = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(typed);
  if (civil !== null) {
    const iso = `${civil[3] ?? ''}-${civil[2] ?? ''}-${civil[1] ?? ''}`;
    return original.endsWith('T00:00:00Z') ? `${iso}T00:00:00Z` : iso;
  }
  return typed;
}

// ---- ranges, sort and time zone in the viewer (owner feedback 2026-11-01) ---------------------

/** A range is a `>=` followed by a `<=` on the same field (the editor's "zwischen"). */
export function isRangeAt(clauses: readonly ReportFilterClause[], index: number): boolean {
  const a = clauses[index];
  const b = clauses[index + 1];
  return (
    a !== undefined &&
    b !== undefined &&
    a.op === '>=' &&
    b.op === '<=' &&
    a.field === b.field &&
    (a.editable === true) === (b.editable === true)
  );
}

/** One filter as the viewer shows it: a clause, or a range of two (`span` 2). */
export interface FilterGroup extends FilterToken {
  readonly span: number;
}

/** The tokens with every range folded into one ("Startdatum 01.01.2026 – 31.03.2026"). */
export function filterGroups(
  saved: ReportFilters,
  applied: ReportFilters,
  values: readonly ReportFilterValue[],
  locale: string,
): FilterGroup[] {
  const tokens = filterTokens(saved, applied, values, locale);
  const out: FilterGroup[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (token === undefined) continue;
    if (applied.join === 'ALL' && isRangeAt(applied.clauses, i)) {
      const from = applied.clauses[i]?.value ?? '';
      const to = applied.clauses[i + 1]?.value ?? '';
      out.push({
        ...token,
        text: `${showValue(from, locale)} – ${showValue(to, locale)}`,
        changed: token.changed || (tokens[i + 1]?.changed ?? false),
        span: 2,
      });
      i++;
      continue;
    }
    out.push({ ...token, span: 1 });
  }
  return out;
}

export interface ViewSort {
  readonly by: string;
  readonly direction: 'ASC' | 'DESC';
}

/** `sort=<by>:<ASC|DESC>` — the by may hold a colon (`sum:prämie`), the direction never does. */
export function sortFromSearch(search: Readonly<Record<string, string>>): ViewSort[] {
  const raw = search.sort;
  if (raw === undefined) return [];
  const at = raw.lastIndexOf(':');
  const by = raw.slice(0, at);
  const direction = raw.slice(at + 1);
  return at > 0 && (direction === 'ASC' || direction === 'DESC') ? [{ by, direction }] : [];
}

/** A header click: ascending, then descending, then the report's own sort again. */
export function nextSort(current: readonly ViewSort[], by: string): ViewSort[] {
  const active = current.find((s) => s.by === by);
  if (active === undefined) return [{ by, direction: 'ASC' }];
  if (active.direction === 'ASC') return [{ by, direction: 'DESC' }];
  return [];
}

export function sortToSearch(sort: readonly ViewSort[]): Record<string, string> {
  const first = sort[0];
  return first === undefined ? {} : { sort: `${first.by}:${first.direction}` };
}

/** `tz=utc` shows and enters date-times in UTC; anything else is the browser's zone. */
export function zoneFromSearch(search: Readonly<Record<string, string>>): 'LOCAL' | 'UTC' {
  return search.tz === 'utc' ? 'UTC' : 'LOCAL';
}

/** The URL entries that are not filter values: they survive a filter change. */
export function viewSearch(search: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(Object.entries(search).filter(([k]) => k === 'sort' || k === 'tz'));
}
